import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { MessageSquareWarning, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { ReviewItem } from "./LatestReviews";

/* Leadership early-warning panel: all 1–2★ Google reviews captured in the
   last 30 days, grouped by location. Informational only — never affects
   escalation, tone tiers or complaint standings. */
export const NegativeReviewsPanel = ({ className, compact = false }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/google-ratings/negative-reviews", { params: { days: 30 } })
      .then((r) => setData(r.data))
      .catch(() => setData(null)) // non-leadership or transient error — hide quietly
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Skeleton className={cn("h-24", className)} data-testid="negative-reviews-skeleton" />;
  if (!data) return null;

  const groups = compact
    ? (data.groups || []).map((g) => ({ ...g, reviews: g.reviews.slice(0, 2) })).slice(0, 3)
    : data.groups || [];

  return (
    <Card className={className} data-testid="negative-reviews-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="font-serif text-base font-normal flex items-center gap-1.5">
            <MessageSquareWarning size={15} className="text-[hsl(var(--cx-crimson))]" />
            Negative Google Reviews — last {data.days} days
          </CardTitle>
          <Badge variant="outline"
            className={cn("tabular text-[0.65rem]",
              data.count > 0
                ? "border-[hsl(var(--cx-crimson)/0.5)] text-[hsl(var(--cx-crimson))]"
                : "border-[hsl(var(--status-green)/0.5)] text-[hsl(var(--status-green))]")}
            data-testid="negative-reviews-count">
            {data.count} review{data.count !== 1 ? "s" : ""}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {data.count === 0 ? (
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground py-2" data-testid="negative-reviews-empty">
            <ShieldCheck size={13} className="text-[hsl(var(--status-green))]" />
            No 1–2★ reviews captured in the last {data.days} days.
          </p>
        ) : (
          <div className="space-y-3">
            {groups.map((g) => (
              <div key={g.location} data-testid={`negative-reviews-group-${g.location}`}>
                <p className="text-[0.68rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground mb-1.5">{g.location}</p>
                <div className="space-y-1.5">
                  {g.reviews.map((r, i) => (
                    <ReviewItem key={r.id || `${g.location}-${i}`} review={{ ...r, negative: true }}
                      testId={`negative-review-item-${g.location}-${i}`} showLocation={false} />
                  ))}
                </div>
              </div>
            ))}
            {compact && (
              <button
                className="text-[0.68rem] font-medium text-[hsl(var(--cx-navy))] underline underline-offset-2 hover:opacity-80"
                onClick={() => navigate("/google-ratings")}
                data-testid="negative-reviews-view-all">
                View all in Google Ratings →
              </button>
            )}
          </div>
        )}
        <p className="text-[0.6rem] text-muted-foreground mt-2.5">
          Best-effort capture — Google's API shares only the ~5 most recent reviews per listing per fetch. Informational only; never affects escalation or standings.
        </p>
      </CardContent>
    </Card>
  );
};
